/**
 * Fetches OncoEMR users for recipient / assignee autocomplete.
 * @module fetchOncoemrUsersSearch
 */

import {
  FetchOncoemrUsersSearchParams,
  OncoemrUser,
} from "../../data-model/oncoemrUsersModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";
import { unwrapBackendListData } from "../unwrapBackendListData";

/**
 * Builds the query string for `GET /oncoemr-tasks/users/search`.
 *
 * @param params - Org scope and optional substring filter and row cap.
 * @returns Encoded query string without leading `?`.
 */
export const buildOncoemrUsersSearchQueryString = (
  params: FetchOncoemrUsersSearchParams,
): string => {
  const searchParams = new URLSearchParams({
    org_id: params.org_id,
  });

  if (params.q !== undefined && params.q !== "") {
    searchParams.set("q", params.q);
  }
  if (params.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }

  return searchParams.toString();
};

/**
 * Searches enabled OncoEMR users for an organization by display name substring.
 *
 * @param params - Org id and optional `q` / `limit` filters.
 * @param signal - Optional abort signal for in-flight cancellation.
 * @returns Users ordered by `first_and_last`.
 * @throws Error when the HTTP response is not OK or the body is not a list envelope.
 */
export const fetchOncoemrUsersSearch = async (
  params: FetchOncoemrUsersSearchParams,
  signal?: AbortSignal,
): Promise<OncoemrUser[]> => {
  const query = buildOncoemrUsersSearchQueryString(params);
  const url = `${API_ENDPOINTS.ONCOEMR_TASKS_USERS_SEARCH}?${query}`;
  const bearerToken = await getBearerToken();

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch OncoEMR users (${response.status} ${response.statusText})`,
    );
  }

  const body: unknown = await response.json();
  return unwrapBackendListData<OncoemrUser>(
    body,
    response.status,
    "OncoEMR users",
  );
};
